import { alertExtreme, alertMild, alertSevere } from '#lib/icons/index.js';
import { mapOpt, optToArr } from '#lib/overview/utils/object.js';
import { convert, parseUnit, type Unit } from '#lib/overview/utils/units.js';
import { count, find, fold } from '#lib/utils/generators.js';
import type { DeepReadonly } from '#lib/utils/type.js';
import { unitMap } from '#lib/utils/units.js';
import {
  aggregateColumn,
  type Column,
  type Columns,
  type FlatColumns,
  type TableRow,
  type TableRowGroup,
  type TableRowLazyGroup,
  type TableRowLeaf,
  type Threshold,
} from '#lib/zod/output/TabularData.js';
import {
  faSortAlphaDown,
  faSortAlphaDownAlt,
  faSortAmountDown,
  faSortAmountDownAlt,
  type IconDefinition as SolidIconDefinition,
} from '@fortawesome/free-solid-svg-icons';
import { type IconDefinition as BrandIconDefinition } from '@fortawesome/free-brands-svg-icons';
import type { FlatRow } from './state.svelte';
import dayjs from 'dayjs';

export type RowSorting =
  | { by: 'name'; inverted?: boolean }
  | { by: 'data'; key: string; inverted?: boolean; peak?: boolean }
  | { by: 'tag'; tag: string; inverted?: boolean }
  | { by: 'parameter'; parameter: string; inverted?: boolean }
  | { by: 'date-like-parameter'; parameter: string; inverted?: boolean };

export const aggregateRows = (
  cols: FlatColumns,
  rows: TableRow[],
  purpose: 'grouping' | 'sorting',
  tagStrategy: 'every' | 'some' = 'some',
): TableRowLeaf => {
  const noRows = rows.length === 0;

  const data: TableRowLeaf['data'] = Object.fromEntries(
    cols
      .filter((c): c is Extract<(typeof cols)[0], { key: string }> => c.type === 'data' || !c.type)
      .map((c) => [
        c.key,
        (noRows
          ? undefined
          : {
              value: aggregateColumn(
                c.aggregationStrategy,
                { key: c.key },
                datafulUpTraverse(rows, [c], () => true, false),
                purpose,
              )!,
              units:
                find(
                  datafulUpTraverse(rows, [c], () => true, false) as Iterable<TableRowLeaf>,
                  (r) => Boolean(r.data?.[c.key]),
                )?.data[c.key]?.units ?? '',
            })!,
      ]),
  );

  const tagSet = new Set<string>();
  for (const row of rows) {
    for (const t of row.tags) tagSet.add(t);
  }

  let tags = [...tagSet];
  if (tagStrategy === 'every') tags = tags.filter((t) => rows.every((r) => r.tags.includes(t)));

  return {
    name: `(${rows.length} traces)`,
    parameters: { count: rows.length },
    data,
    tags,
    type: 'leaf',
  };
};

export const minmaxRows = (
  cols: FlatColumns,
  rows: TableRow[],
  strategy: 'min' | 'max',
  purpose: 'grouping' | 'sorting',
): TableRowLeaf => {
  const noRows = rows.length === 0;
  const data: TableRowLeaf['data'] = Object.fromEntries(
    cols
      .filter((c): c is Extract<(typeof cols)[0], { key: string }> => c.type === 'data' || !c.type)
      .map((c) => [
        c.key,
        (noRows
          ? undefined
          : {
              value: aggregateColumn(
                strategy,
                { key: c.key },
                bottomUpTraverse(rows, (g) => !('children' in g), false),
                purpose,
              )!,
              units:
                find(
                  bottomUpTraverse(
                    rows,
                    (g) => !('children' in g),
                    false,
                  ) as Iterable<TableRowLeaf>,
                  (r) => Boolean(r.data[c.key]),
                )?.data[c.key]?.units ?? '',
            })!,
      ]),
  );

  return {
    name: `(${rows.length} traces)`,
    data,
    parameters: {},
    tags: [],
    type: 'leaf',
  };
};

export const sortingIcon = (
  sorting: 'asc' | 'desc' | undefined | false,
  col: FlatColumns[0] | { type: 'name' },
) =>
  !sorting
    ? undefined
    : 'key' in col
      ? sorting === 'asc'
        ? faSortAmountDownAlt
        : faSortAmountDown
      : col.type === 'name' || col.type === 'parameter' || col.type === 'date-like-parameter'
        ? sorting === 'asc'
          ? faSortAlphaDown
          : faSortAlphaDownAlt
        : undefined;

export const colGroupBounds = (cols: DeepReadonly<Columns>) => {
  const result: Array<{ start: number; end: number }> = [];
  let i = 0;
  for (const col of cols) {
    if ('columns' in col) {
      result.push({ start: i, end: i + col.columns.length });
      i += col.columns.length;
    } else {
      i += 1;
    }
  }
  return result;
};

export const alertIcon = (severity: 1 | 2 | 3): SolidIconDefinition => {
  switch (severity) {
    case 1:
      return alertMild;
    case 2:
      return alertSevere;
    case 3:
      return alertExtreme;
  }
};

export const iconAsRawHtml = (icon: SolidIconDefinition | BrandIconDefinition): string => {
  const [width, height, _, __, path] = icon.icon;
  const d = Array.isArray(path) ? path.join(' ') : path;
  return `
        <svg
            class="svelte-fa svelte-1cj2gr0"
            style="height:1em;vertical-align:-.125em;transform-origin:center;overflow:visible"
            viewBox="0 0 ${width} ${height}"
            aria-hidden="true"
            role="img"
            xmlns="http://www.w3.org/2000/svg"
        >
            <g transform="translate(${width / 2} ${height / 2})" transform-origin="${width / 4} 0">
                <g transform="translate(0,0) scale(1,1)">
                    <path
                        d="${d}"
                        fill="currentColor"
                        transform="translate(-${width / 2} -${height / 2})"
                    ></path>
                </g>
            </g>
        </svg>
    `;
};
export const alertIconAsRawHtml = (severity: 1 | 2 | 3): string => `
    <span class="threshold-alert" data-severity="${severity}">
        ${iconAsRawHtml(alertIcon(severity))}
    </span>
`;
export const peakIconAsRawHtml = () =>
  `<span class="text-red-500 align-baseline inline-block"><svg class="svelte-fa svelte-1cj2gr0" style="height:1em;font-size:.75em;vertical-align:-.125em;transform-origin:center;overflow:visible" viewBox="0 0 384 512" aria-hidden="true" role="img" xmlns="http://www.w3.org/2000/svg"><g transform="translate(192 256)" transform-origin="96 0"><g transform="translate(0,0) scale(1,1)"><path d="M169.4 137.4c12.5-12.5 32.8-12.5 45.3 0l160 160c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L192 205.3 54.6 342.6c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3l160-160z" fill="currentColor" transform="translate(-192 -256)"></path></g></g></svg></span>`;

export const firstOrdinaryRowIndex = (
  showTitle: boolean,
  showCheckboxes: boolean,
  hasAlerts: boolean,
) => +showTitle + +showCheckboxes + +hasAlerts;

const defaultFormat: Intl.NumberFormatOptions = {
  maximumFractionDigits: 1,
  minimumFractionDigits: 1,
};
export const formatValue = (value: number, format?: Intl.NumberFormatOptions) =>
  value.toLocaleString('cs', format ?? defaultFormat);

export const formatUnit = (units: string) => unitMap[units] ?? units;

export const formatThresholdCondition = ([cond, value]: Threshold['if'], units: string) => {
  const c = (() => {
    switch (cond) {
      case '<':
        return 'strictly under';
      case '<=':
        return 'under';
      case '>=':
        return 'over';
      case '>':
        return 'strictly over';
    }
  })();

  return `${c} ${formatValue(value)} ${formatUnit(units)}`;
};

export const formatThresholdTooltip = (t: Threshold, col: FlatColumns[number]) => {
  return [
    alertIconAsRawHtml(t.urgency),
    ...optToArr(col.group?.title),
    col.title,
    formatThresholdCondition(t.if, '%'), // FIXME use actual unit
  ].join(' ');
};

export const formatHeaderTooltip = (col: FlatColumns[number]) => {
  const alertDesc =
    'thresholds' in col
      ? col.thresholds?.map((t) => formatThresholdTooltip(t, col)).join('<br/>')
      : undefined;

  const colDesc = mapOpt(col.description, (d) => d.replaceAll('$PEAK_ICON$', peakIconAsRawHtml()));

  return [...optToArr(colDesc), ...optToArr(alertDesc)].join('<br/><br/>');
};

export function calculateCountHint(
  row: TableRowGroup,
): Exclude<TableRowLazyGroup['countHint'], undefined> {
  const realLeafs = count(bottomUpTraverse(row.children, (r) => r.type === 'leaf', false, false));
  const lazyLeafs = fold(
    bottomUpTraverse(
      row.children,
      (r) => r.type === 'lazyGroup' && r.children === undefined,
      false,
      false,
    ),
    (prev, curr) => {
      if (curr.type === 'lazyGroup') {
        return prev + (curr.countHint?.leafs ?? 0);
      } else {
        return prev;
      }
    },
    0,
  );

  return {
    subgroups: row.children.length,
    leafs: realLeafs + lazyLeafs,
  };
}

export type TableRowFilter = (row: TableRow) => boolean;

export function* traverseTable(
  table: Iterable<TableRow>,
  filter?: TableRowFilter,
): Generator<TableRow> {
  for (const row of table) {
    if (filter && !filter(row)) continue;

    yield row;
    if ('children' in row && row.children !== undefined) yield* traverseTable(row.children, filter);
  }
}

/// Traverses from the deepest rows that have data for all specified columns up
export function* datafulUpTraverse(
  table: Iterable<TableRow>,
  cols: FlatColumns,
  filter?: TableRowFilter,
  emitGroupIfValidChildren: boolean = true,
  emitChildrenIfValidGroup: boolean = false,
): Generator<TableRow> {
  for (const row of table) {
    if (
      row.data !== undefined &&
      cols.every((c) => !('key' in c && c.key !== undefined) || c.key in row.data!)
    )
      yield row;
    else {
      if ('children' in row && row.children !== undefined) {
        const children = bottomUpTraverse(
          row.children,
          filter,
          emitGroupIfValidChildren,
          emitChildrenIfValidGroup,
        );
        const first = children.next();

        if (!first.done) {
          yield first.value;
          yield* children;

          if (emitGroupIfValidChildren) {
            yield row;
            continue;
          }
        } else if (emitChildrenIfValidGroup) {
          if (filter && !filter(row)) continue;

          yield* bottomUpTraverse(row.children, undefined);
          yield row;
          continue;
        }
      }

      if (filter && !filter(row)) continue;

      yield row;
    }
  }
}

export function* bottomUpTraverse(
  table: Iterable<TableRow>,
  filter?: TableRowFilter,
  emitGroupIfValidChildren: boolean = true,
  emitChildrenIfValidGroup: boolean = false,
): Generator<TableRow> {
  for (const row of table) {
    if ('children' in row && row.children !== undefined) {
      const children = bottomUpTraverse(
        row.children,
        filter,
        emitGroupIfValidChildren,
        emitChildrenIfValidGroup,
      );
      const first = children.next();

      if (!first.done) {
        yield first.value;
        yield* children;

        if (emitGroupIfValidChildren) {
          yield row;
          continue;
        }
      } else if (emitChildrenIfValidGroup) {
        if (filter && !filter(row)) continue;

        yield* bottomUpTraverse(row.children, undefined);
        yield row;
        continue;
      }
    }

    if (filter && !filter(row)) continue;

    yield row;
  }
}

export const formatDayjs = (d: unknown, format: string): string => {
  return dayjs(d as any).format(format);
};

// Sort comparators moved to DataTable.state.svelte.ts (compareByValue, compareByPeak)

export function convertTableRowUnits(row: TableRow, desiredUnits: Unit): TableRow {
  const rowData = row.data;
  if (rowData === undefined) return row;
  const loweredData = Object.fromEntries(
    Object.entries(rowData).map(([key, data]) => {
      return [
        key,
        {
          value: convert(data.value, parseUnit(data.units ?? '1'), desiredUnits),
          peak:
            data.peak !== undefined
              ? convert(data.peak, parseUnit(data.units ?? '1'), desiredUnits)
              : undefined,
          units: desiredUnits.toString(),
          date: data.date,
        },
      ];
    }),
  ) as TableRow['data'];
  return {
    ...row,
    data: loweredData!,
  };
}

export type TableDownloadOptions = {
  namingFunction?: (depth: number) => string;
  rowFilter?: (row: FlatRow, index: number) => boolean;
  separator?: string;
  newline?: string;
  undefinedPlaceholder?: string;
  customColHandler?: {
    customColumnNames: (col: DeepReadonly<Extract<Column, { type: 'custom' }>>) => string[];
    customColToValue: (
      r: TableRow,
      col: DeepReadonly<Extract<Column, { type: 'custom' }>>,
    ) => (string | undefined)[];
  };
};

export function tableToCsvStream(
  rows: FlatRow[],
  cols: DeepReadonly<FlatColumns>,
  {
    namingFunction = (i) => `${i}`,
    separator = ',',
    newline = '\n',
    undefinedPlaceholder = '',
    rowFilter = () => true,
    customColHandler,
  }: TableDownloadOptions = {},
): Generator<string> {
  const columnLeaves = cols.filter((c) => !('columns' in c));
  const maxDepth = rows.reduce((acc: number, curr: FlatRow) => Math.max(acc, curr.depth), 0);

  const colsWithPeaks = new Set(
    Array.from({ length: maxDepth + 1 }, (_, i) => i)
      .flatMap((depth) =>
        Object.entries(rows.find((r: FlatRow) => r.depth === depth)?.original.data ?? {}),
      )
      .filter(([_, data]) => data !== undefined && 'peak' in data)
      .map(([key, _]) => key),
  );

  const header = [
    ...Array.from({ length: maxDepth + 1 }, (_, i) => i).map(namingFunction),
    ...columnLeaves.flatMap((col) => {
      if (col.type === 'custom' && customColHandler !== undefined)
        return customColHandler.customColumnNames(col);
      const res = [col.title];
      if ((col.type === 'data' || !col.type) && colsWithPeaks.has(col.key))
        res.push(`peak ${col.title}`);
      return res;
    }),
  ];

  const colToString = (row: TableRow, c: DeepReadonly<Column>) => {
    if (c.type === 'parameter') return [row.parameters[c.parameter]];
    if (c.type === 'data' || !c.type) {
      const res = [row.data?.[c.key]?.value];
      if (colsWithPeaks.has(c.key)) res.push(row.data?.[c.key]?.peak);
      return res;
    }
    if (c.type === 'date-like-parameter') {
      const date = row.parameters[c.parameter];
      return [date === undefined ? undefined : formatDayjs(date, c.displayFormat)];
    }
    if (c.type === 'hasTag') return [row.tags.includes(c.tag)];
    if (c.type === 'custom') {
      if (customColHandler === undefined) {
        return [undefined];
      }
      return customColHandler.customColToValue(row, c);
    }

    return [undefined];
  };

  function* generator() {
    yield `${header.join(separator)}${newline}`;

    let names: (string | undefined)[] = Array.from({ length: maxDepth + 1 }, () => undefined);

    for (const [i, row] of rows.entries()) {
      names = names.map((n, j) =>
        j < row.depth ? n : j === row.depth ? row.original.name : undefinedPlaceholder,
      );
      if (!rowFilter(row, i)) continue;
      yield `${[...names, ...columnLeaves.flatMap((c) => colToString(row.original, c)).map((v) => v ?? undefinedPlaceholder)].join(separator)}${newline}`;
    }
  }

  return generator();
}

export function flattenCols(cols: Columns): FlatColumns {
  return cols.flatMap((g) => ('columns' in g ? g.columns.map((c) => ({ ...c, group: g })) : g));
}
